import { useState } from 'react';
import type { BriefTheme } from '@mg/shared';
import type { ThemeIcon } from './themeIcon';

/**
 * The images a Social Trends card may try, best first.
 *
 * A theme carries every usable image its sources offer; a brief generated before that carries only
 * its one pick. Either way the list is what the card walks through when an image fails to load.
 */
export function themeImageSources(theme: Pick<BriefTheme, 'imageUrl' | 'imageCandidates'>): string[] {
  if (theme.imageCandidates && theme.imageCandidates.length > 0) return theme.imageCandidates;
  return theme.imageUrl ? [theme.imageUrl] : [];
}

/**
 * A theme's picture — or, when there is none that loads, its placeholder.
 *
 * Third-party image links break: a feed publishes a malformed URL, a CDN link expires, a "media"
 * field turns out to be a video player. Rendering one bare showed the browser's broken-image box
 * with the alt text spilling out of it. So each failure moves on to the theme's next image, and when
 * none is left the card shows the same icon tile it uses for a theme with no image at all.
 *
 * Key it by theme, so a regenerated card starts again from its first image.
 */
export function ThemeThumbnail({
  sources,
  alt,
  fallback,
}: {
  sources: string[];
  alt: string;
  fallback: ThemeIcon;
}) {
  // Which image is being tried; past the end means none of them loaded.
  const [index, setIndex] = useState(0);
  const src = sources[index];

  if (!src) {
    const { Icon, color } = fallback;
    return (
      <div
        className="flex h-[6rem] w-[7.5rem] shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${color}1f` }}
        aria-hidden="true"
        data-thumbnail="placeholder"
      >
        <Icon weight="duotone" color={color} size="2.625rem" />
      </div>
    );
  }
  return (
    <img
      // Keyed by URL so a failed image is replaced, not re-tried in place.
      key={src}
      src={src}
      alt={alt}
      width={120}
      height={96}
      loading="lazy"
      onError={() => setIndex((i) => i + 1)}
      // White behind the picture itself, so a transparent one (a publisher's dark logo, say) stays
      // legible on a dark card. An opaque picture covers it completely.
      className="h-[6rem] w-[7.5rem] shrink-0 rounded-lg bg-white object-cover"
    />
  );
}
